import React from 'react';
import { Box, Collapse, IconButton, InputBase, useTheme, Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import StoreIcon from '@mui/icons-material/Store';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import GroupIcon from '@mui/icons-material/Group';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';

interface MobileSearchPanelProps {
  open: boolean;
  value: string;
  placeholder: string;
  onChange: (val: string) => void;
  onSubmit: () => void;
  onClose: () => void;
  onSubmitWithScope?: (scope: 'Produkte' | 'Angebote' | 'Mitglieder' | 'Hilfe-Center' | 'Nach Ort') => void;
}

const MobileSearchPanel: React.FC<MobileSearchPanelProps> = ({ open, value, placeholder, onChange, onSubmit, onClose, onSubmitWithScope }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [scope, setScope] = React.useState<'Produkte' | 'Angebote' | 'Mitglieder' | 'Hilfe-Center' | 'Nach Ort'>('Produkte');
  const openMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);
  const selectScope = (s: typeof scope) => { setScope(s); closeMenu(); };

  const effectivePlaceholder = (() => {
    switch (scope) {
      case 'Produkte':
        return placeholder;
      case 'Angebote':
        return 'In Angeboten suchen...';
      case 'Mitglieder':
        return 'Mitglieder suchen...';
      case 'Hilfe-Center':
        return 'Im Hilfe-Center suchen...';
      case 'Nach Ort':
        return 'Nach Ort suchen...';
      default:
        return placeholder;
    }
  })();
  return (
    <Collapse in={open} timeout={250} unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          top: 64, // below AppBar
          left: 0,
          right: 0,
          zIndex: (t) => t.zIndex.modal + 2,
          px: 2,
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 3,
            boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
          }}
        >
          <Button
            onClick={openMenu}
            endIcon={<ArrowDropDownIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              height: 40,
              px: 1.5,
              color: 'text.primary',
              border: '1px solid rgba(0,0,0,0.12)',
              backgroundColor: 'rgba(0,0,0,0.02)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
              minWidth: 120,
            }}
          >
            {scope}
          </Button>
          <InputBase
            autoFocus
            placeholder={effectivePlaceholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            sx={{
              flex: 1,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              backgroundColor: 'rgba(0,0,0,0.04)',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (onSubmitWithScope) {
                  onSubmitWithScope(scope);
                } else {
                  onSubmit();
                }
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={() => {
              if (onSubmitWithScope) {
                onSubmitWithScope(scope);
              } else {
                onSubmit();
              }
            }}
            aria-label="search"
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
          >
            <SearchIcon />
          </IconButton>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { borderRadius: 2, mt: 1 } }}
        >
          <MenuItem onClick={() => selectScope('Produkte')} selected={scope === 'Produkte'}>
            <ListItemIcon><StoreIcon /></ListItemIcon>
            <ListItemText primary="Produkte" />
          </MenuItem>
          <MenuItem onClick={() => selectScope('Angebote')} selected={scope === 'Angebote'}>
            <ListItemIcon><LocalOfferIcon /></ListItemIcon>
            <ListItemText primary="Angebote" />
          </MenuItem>
          <MenuItem onClick={() => selectScope('Mitglieder')} selected={scope === 'Mitglieder'}>
            <ListItemIcon><GroupIcon /></ListItemIcon>
            <ListItemText primary="Mitglieder" />
          </MenuItem>
          <MenuItem onClick={() => selectScope('Hilfe-Center')} selected={scope === 'Hilfe-Center'}>
            <ListItemIcon><HelpOutlineIcon /></ListItemIcon>
            <ListItemText primary="Hilfe-Center" />
          </MenuItem>
          <MenuItem onClick={() => selectScope('Nach Ort')} selected={scope === 'Nach Ort'}>
            <ListItemIcon><LocationOnIcon /></ListItemIcon>
            <ListItemText primary="Nach Ort" />
          </MenuItem>
        </Menu>
      </Box>
    </Collapse>
  );
};

export default MobileSearchPanel;
